import {
  ArrowRightIcon,
  BriefcaseIcon,
  CaretDownIcon,
  CheckIcon,
  FileTextIcon,
  GraduationCapIcon,
  MagnifyingGlassIcon,
  PaperPlaneTiltIcon,
  StudentIcon,
} from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";
import { useEffect, useId, useMemo, useRef, useState } from "react";

import type { ApplicationType } from "../types";

type Props = {
  targetJob: string;
  sector: string;
  applicationType: ApplicationType | null;
  jobDescription: string;
  onTargetJobChange: (value: string) => void;
  onSectorChange: (value: string) => void;
  onApplicationTypeChange: (value: ApplicationType) => void;
  onJobDescriptionChange: (value: string) => void;
  onContinue: () => void;
};

type JobOption = {
  title: string;
  sector: string;
};

const applicationTypes = [
  {
    value: "job",
    label: "Emploi",
    icon: BriefcaseIcon,
  },
  {
    value: "internship",
    label: "Stage",
    icon: GraduationCapIcon,
  },
  {
    value: "work-study",
    label: "Alternance",
    icon: StudentIcon,
  },
  {
    value: "spontaneous",
    label: "Candidature spontanée",
    icon: PaperPlaneTiltIcon,
  },
] satisfies Array<{
  value: ApplicationType;
  label: string;
  icon: typeof BriefcaseIcon;
}>;

const sectors = [
  "Administration et services publics",
  "Agriculture et environnement",
  "Banque, finance et assurance",
  "Bâtiment et construction",
  "Commerce et vente",
  "Communication et marketing",
  "Comptabilité et gestion",
  "Conseil et services aux entreprises",
  "Droit et services juridiques",
  "Éducation et formation",
  "Énergie et ressources naturelles",
  "Hôtellerie, restauration et tourisme",
  "Industrie et production",
  "Informatique et numérique",
  "Médias, création et audiovisuel",
  "Ressources humaines",
  "Santé et action sociale",
  "Sécurité et défense",
  "Télécommunications",
  "Transport et logistique",
  "Autre secteur",
] as const;

const jobOptions: JobOption[] = [
  {
    title: "Administrateur système",
    sector: "Informatique et numérique",
  },
  {
    title: "Agent administratif",
    sector: "Administration et services publics",
  },
  {
    title: "Agent commercial",
    sector: "Commerce et vente",
  },
  {
    title: "Agent de sécurité",
    sector: "Sécurité et défense",
  },
  {
    title: "Agronome",
    sector: "Agriculture et environnement",
  },
  {
    title: "Analyste de données",
    sector: "Informatique et numérique",
  },
  {
    title: "Analyste financier",
    sector: "Banque, finance et assurance",
  },
  {
    title: "Architecte",
    sector: "Bâtiment et construction",
  },
  {
    title: "Assistant administratif",
    sector: "Administration et services publics",
  },
  {
    title: "Assistant comptable",
    sector: "Comptabilité et gestion",
  },
  {
    title: "Assistant de direction",
    sector: "Administration et services publics",
  },
  {
    title: "Assistant ressources humaines",
    sector: "Ressources humaines",
  },
  {
    title: "Auditeur",
    sector: "Comptabilité et gestion",
  },
  {
    title: "Avocat",
    sector: "Droit et services juridiques",
  },
  {
    title: "Caissier",
    sector: "Commerce et vente",
  },
  {
    title: "Chargé de clientèle",
    sector: "Commerce et vente",
  },
  {
    title: "Chargé de communication",
    sector: "Communication et marketing",
  },
  {
    title: "Chargé de recrutement",
    sector: "Ressources humaines",
  },
  {
    title: "Chef comptable",
    sector: "Comptabilité et gestion",
  },
  {
    title: "Chef de chantier",
    sector: "Bâtiment et construction",
  },
  {
    title: "Chef de projet",
    sector: "Conseil et services aux entreprises",
  },
  {
    title: "Community manager",
    sector: "Communication et marketing",
  },
  {
    title: "Comptable",
    sector: "Comptabilité et gestion",
  },
  {
    title: "Conseiller clientèle",
    sector: "Banque, finance et assurance",
  },
  {
    title: "Consultant",
    sector: "Conseil et services aux entreprises",
  },
  {
    title: "Créateur de contenu",
    sector: "Médias, création et audiovisuel",
  },
  {
    title: "Développeur backend",
    sector: "Informatique et numérique",
  },
  {
    title: "Développeur frontend",
    sector: "Informatique et numérique",
  },
  {
    title: "Développeur full stack",
    sector: "Informatique et numérique",
  },
  {
    title: "Développeur mobile",
    sector: "Informatique et numérique",
  },
  {
    title: "Designer graphique",
    sector: "Médias, création et audiovisuel",
  },
  {
    title: "DevOps",
    sector: "Informatique et numérique",
  },
  {
    title: "Électricien",
    sector: "Bâtiment et construction",
  },
  {
    title: "Enseignant",
    sector: "Éducation et formation",
  },
  {
    title: "Formateur",
    sector: "Éducation et formation",
  },
  {
    title: "Gestionnaire de paie",
    sector: "Ressources humaines",
  },
  {
    title: "Infirmier",
    sector: "Santé et action sociale",
  },
  {
    title: "Ingénieur agronome",
    sector: "Agriculture et environnement",
  },
  {
    title: "Ingénieur civil",
    sector: "Bâtiment et construction",
  },
  {
    title: "Ingénieur logiciel",
    sector: "Informatique et numérique",
  },
  {
    title: "Juriste",
    sector: "Droit et services juridiques",
  },
  {
    title: "Logisticien",
    sector: "Transport et logistique",
  },
  {
    title: "Médecin",
    sector: "Santé et action sociale",
  },
  {
    title: "Motion designer",
    sector: "Médias, création et audiovisuel",
  },
  {
    title: "Pharmacien",
    sector: "Santé et action sociale",
  },
  {
    title: "Product designer",
    sector: "Informatique et numérique",
  },
  {
    title: "Responsable commercial",
    sector: "Commerce et vente",
  },
  {
    title: "Responsable marketing",
    sector: "Communication et marketing",
  },
  {
    title: "Responsable ressources humaines",
    sector: "Ressources humaines",
  },
  {
    title: "Secrétaire",
    sector: "Administration et services publics",
  },
  {
    title: "Spécialiste cybersécurité",
    sector: "Informatique et numérique",
  },
  {
    title: "Technicien informatique",
    sector: "Informatique et numérique",
  },
  {
    title: "Technicien réseau",
    sector: "Télécommunications",
  },
  {
    title: "Téléconseiller",
    sector: "Télécommunications",
  },
  {
    title: "UI Designer",
    sector: "Informatique et numérique",
  },
  {
    title: "UX Designer",
    sector: "Informatique et numérique",
  },
  {
    title: "Vendeur",
    sector: "Commerce et vente",
  },
];

const normalizeText = (value: string) => {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("fr")
    .trim();
};

const getJobScore = (title: string, query: string) => {
  const normalizedTitle = normalizeText(title);

  if (normalizedTitle === query) {
    return 0;
  }

  if (normalizedTitle.startsWith(query)) {
    return 1;
  }

  if (normalizedTitle.split(/\s+/).some((word) => word.startsWith(query))) {
    return 2;
  }

  return 3;
};

export const ObjectiveStep = ({
  targetJob,
  sector,
  applicationType,
  jobDescription,
  onTargetJobChange,
  onSectorChange,
  onApplicationTypeChange,
  onJobDescriptionChange,
  onContinue,
}: Props) => {
  const comboboxId = useId();

  const comboboxRef = useRef<HTMLDivElement>(null);

  const [suggestionsOpen, setSuggestionsOpen] = useState(false);

  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(0);

  const normalizedTargetJob = normalizeText(targetJob);

  const exactJob = useMemo(
    () => jobOptions.find((job) => normalizeText(job.title) === normalizedTargetJob),
    [normalizedTargetJob],
  );

  const suggestions = useMemo(() => {
    if (normalizedTargetJob.length < 1) {
      return jobOptions.slice(0, 7);
    }

    return jobOptions
      .filter((job) => normalizeText(job.title).includes(normalizedTargetJob))
      .sort(
        (first, second) =>
          getJobScore(first.title, normalizedTargetJob) -
          getJobScore(second.title, normalizedTargetJob),
      )
      .slice(0, 7);
  }, [normalizedTargetJob]);

  const suggestedSectors = useMemo(() => {
    const values = exactJob ? [exactJob.sector] : suggestions.map((job) => job.sector);

    return Array.from(new Set(values));
  }, [exactJob, suggestions]);

  const remainingSectors = useMemo(
    () => sectors.filter((item) => !suggestedSectors.includes(item)),
    [suggestedSectors],
  );

  const complete = targetJob.trim().length >= 2 && Boolean(sector && applicationType);

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      const element = comboboxRef.current;

      if (element && !element.contains(event.target as Node)) {
        setSuggestionsOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, []);

  useEffect(() => {
    setActiveSuggestionIndex(0);
  }, [normalizedTargetJob]);

  const selectJob = (job: JobOption) => {
    onTargetJobChange(job.title);
    onSectorChange(job.sector);
    setSuggestionsOpen(false);
    setActiveSuggestionIndex(0);
  };

  const handleTargetJobChange = (value: string) => {
    /*
     * Lorsque l’utilisateur modifie un
     * poste précédemment sélectionné,
     * l’ancien secteur automatique est
     * retiré pour éviter une incohérence.
     */
    if (exactJob && sector === exactJob.sector) {
      onSectorChange("");
    }

    onTargetJobChange(value);
    setSuggestionsOpen(true);
  };

  const handleJobKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();

      if (!suggestionsOpen) {
        setSuggestionsOpen(true);
        return;
      }

      setActiveSuggestionIndex((current) => Math.min(current + 1, suggestions.length - 1));

      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();

      setActiveSuggestionIndex((current) => Math.max(current - 1, 0));

      return;
    }

    if (event.key === "Enter" && suggestionsOpen && suggestions[activeSuggestionIndex]) {
      event.preventDefault();

      selectJob(suggestions[activeSuggestionIndex]);

      return;
    }

    if (event.key === "Escape") {
      setSuggestionsOpen(false);
    }
  };

  return (
    <section className="mx-auto w-full max-w-3xl">
      <header>
        <h1
          id="create-resume-title"
          className={cn(
            "text-2xl font-bold tracking-tight",
            "leading-tight text-indigo-950",
            "sm:text-3xl lg:text-[34px]",
          )}
        >
          Quel est votre objectif&nbsp;?
        </h1>

        <p className={cn("mt-1 text-xs leading-5 text-indigo-950/75", "sm:text-sm")}>
          Nous personnaliserons les modèles et les conseils selon le poste visé.
        </p>
      </header>

      <div className={cn("mt-3 grid gap-2.5", "sm:mt-4 sm:gap-3")}>
        <div ref={comboboxRef} className="relative">
          <label
            htmlFor={`${comboboxId}-input`}
            className={cn("block text-[11px] font-bold text-indigo-950", "sm:text-xs")}
          >
            Poste recherché
          </label>

          <div className="relative mt-1.5">
            <MagnifyingGlassIcon
              aria-hidden
              size={18}
              weight="duotone"
              className={cn(
                "pointer-events-none absolute left-3 top-1/2",
                "-translate-y-1/2 text-indigo-400",
              )}
            />

            <input
              id={`${comboboxId}-input`}
              type="text"
              role="combobox"
              autoComplete="off"
              aria-autocomplete="list"
              aria-expanded={suggestionsOpen}
              aria-controls={`${comboboxId}-listbox`}
              aria-activedescendant={
                suggestionsOpen && suggestions[activeSuggestionIndex]
                  ? `${comboboxId}-option-${activeSuggestionIndex}`
                  : undefined
              }
              value={targetJob}
              onFocus={() => {
                setSuggestionsOpen(true);
              }}
              onChange={(event) => {
                handleTargetJobChange(event.target.value);
              }}
              onKeyDown={handleJobKeyDown}
              placeholder="Ex. Développeur frontend"
              className={cn(
                "min-h-10 w-full rounded-xl",
                "border border-slate-200 bg-white",
                "pl-10 pr-10",
                "text-xs font-medium text-indigo-950",
                "outline-none transition",
                "placeholder:text-slate-400",
                "hover:border-indigo-200",
                "focus:border-indigo-500 focus:ring-2",
                "focus:ring-indigo-100",
                "sm:min-h-11 sm:text-sm",
              )}
            />

            <CaretDownIcon
              aria-hidden
              size={16}
              weight="bold"
              className={cn(
                "pointer-events-none absolute right-3 top-1/2",
                "-translate-y-1/2 text-slate-400",
                "transition-transform duration-200",
                suggestionsOpen && "rotate-180 text-indigo-500",
              )}
            />
          </div>

          {suggestionsOpen && (
            <div
              id={`${comboboxId}-listbox`}
              role="listbox"
              aria-label="Postes suggérés"
              className={cn(
                "absolute inset-x-0 top-full z-50 mt-1.5",
                "max-h-56 overflow-y-auto overflow-x-hidden",
                "overscroll-contain rounded-xl",
                "border border-indigo-100 bg-white p-1.5",
                "shadow-[0_16px_38px_rgba(15,23,42,0.16)]",
                "[scrollbar-color:rgb(129_140_248)_transparent]",
                "[scrollbar-width:thin]",
                "[&::-webkit-scrollbar]:w-1.5",
                "[&::-webkit-scrollbar-track]:bg-transparent",
                "[&::-webkit-scrollbar-thumb]:rounded-full",
                "[&::-webkit-scrollbar-thumb]:bg-indigo-300",
              )}
            >
              {suggestions.length > 0 ? (
                suggestions.map((job, index) => {
                  const active = index === activeSuggestionIndex;

                  const selected = exactJob?.title === job.title;

                  return (
                    <button
                      id={`${comboboxId}-option-${index}`}
                      key={job.title}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      onMouseEnter={() => {
                        setActiveSuggestionIndex(index);
                      }}
                      onMouseDown={(event) => {
                        event.preventDefault();
                      }}
                      onClick={() => {
                        selectJob(job);
                      }}
                      className={cn(
                        "flex min-h-11 w-full items-center gap-3",
                        "rounded-lg px-3 text-left",
                        "transition-colors",
                        active ? "bg-indigo-50" : "hover:bg-slate-50",
                      )}
                    >
                      <span
                        className={cn(
                          "grid size-8 shrink-0 place-items-center",
                          "rounded-lg",
                          selected
                            ? "bg-emerald-100 text-emerald-600"
                            : "bg-violet-50 text-indigo-600",
                        )}
                      >
                        {selected ? (
                          <CheckIcon aria-hidden size={17} weight="bold" />
                        ) : (
                          <BriefcaseIcon aria-hidden size={17} weight="duotone" />
                        )}
                      </span>

                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-xs font-bold text-indigo-950 sm:text-sm">
                          {job.title}
                        </span>

                        <span className="mt-0.5 block truncate text-[9px] text-slate-500 sm:text-[10px]">
                          {job.sector}
                        </span>
                      </span>
                    </button>
                  );
                })
              ) : (
                <div className="px-3 py-3">
                  <p className="text-xs font-bold text-indigo-950">Aucun poste identique trouvé</p>

                  <p className="mt-1 text-[10px] leading-4 text-slate-500">
                    Vous pouvez conserver votre propre intitulé et continuer la saisie.
                  </p>
                </div>
              )}

              {targetJob.trim().length >= 2 && (
                <div className={cn("mt-1 border-t border-slate-100", "px-3 pb-1 pt-2")}>
                  <p className="text-[9px] leading-4 text-slate-500">
                    Vous ne trouvez pas le poste exact ? Votre saisie personnalisée sera conservée.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <label className={cn("block text-[11px] font-bold text-indigo-950", "sm:text-xs")}>
          Secteur d’activité
          <span className="relative mt-1.5 block">
            <select
              value={sector}
              onChange={(event) => {
                onSectorChange(event.target.value);
              }}
              className={cn(
                "min-h-10 w-full appearance-none rounded-xl",
                "border border-slate-200 bg-white",
                "px-3 pr-10",
                "text-xs font-medium text-indigo-950",
                "outline-none transition",
                "hover:border-indigo-200",
                "focus:border-indigo-500 focus:ring-2",
                "focus:ring-indigo-100",
                "sm:min-h-11 sm:px-4 sm:pr-10 sm:text-sm",
              )}
            >
              <option value="">Choisir un secteur</option>

              {suggestedSectors.length > 0 && (
                <optgroup label="Secteurs recommandés">
                  {suggestedSectors.map((item) => (
                    <option key={`suggested-${item}`} value={item}>
                      {item}
                    </option>
                  ))}
                </optgroup>
              )}

              <optgroup label="Tous les secteurs">
                {remainingSectors.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </optgroup>
            </select>

            <CaretDownIcon
              aria-hidden
              size={16}
              weight="bold"
              className={cn(
                "pointer-events-none absolute right-3 top-1/2",
                "-translate-y-1/2 text-slate-400",
              )}
            />
          </span>
          {exactJob && sector === exactJob.sector && (
            <span
              className={cn(
                "mt-1.5 flex items-center gap-1.5",
                "text-[9px] font-medium text-emerald-600",
                "sm:text-[10px]",
              )}
            >
              <CheckIcon aria-hidden size={12} weight="bold" className="shrink-0" />
              Secteur recommandé selon le poste sélectionné
            </span>
          )}
        </label>
      </div>

      <fieldset className={cn("mt-3", "sm:mt-4")}>
        <legend className={cn("text-[11px] font-bold text-indigo-950", "sm:text-xs")}>
          Type de candidature
        </legend>

        <div className={cn("mt-1.5 grid grid-cols-2 gap-2", "sm:grid-cols-4 sm:gap-2.5")}>
          {applicationTypes.map(({ value, label, icon: IconComponent }) => {
            const active = applicationType === value;

            return (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => {
                  onApplicationTypeChange(value);
                }}
                className={cn(
                  "group relative flex min-h-[58px]",
                  "items-center gap-2 rounded-xl border-2",
                  "px-2.5 text-left",
                  "transition-all duration-200",
                  "hover:-translate-y-0.5",
                  "focus-visible:outline-none focus-visible:ring-2",
                  "focus-visible:ring-indigo-500 focus-visible:ring-offset-1",
                  "sm:min-h-[78px] sm:flex-col",
                  "sm:justify-center sm:gap-1.5 sm:text-center",
                  active
                    ? "border-emerald-400 bg-emerald-50/70 shadow-[0_5px_14px_rgba(16,185,129,0.08)]"
                    : "border-slate-200 bg-white hover:border-indigo-200",
                )}
              >
                <span
                  aria-hidden
                  className={cn(
                    "absolute left-2 top-2 size-3.5",
                    "rounded-full border-2 bg-white",
                    "sm:size-4",
                    active ? "border-[4px] border-emerald-500" : "border-transparent",
                  )}
                />

                <span
                  className={cn(
                    "grid size-8 shrink-0 place-items-center",
                    "rounded-lg",
                    active ? "bg-emerald-100 text-emerald-600" : "bg-indigo-50 text-indigo-700",
                  )}
                >
                  <IconComponent aria-hidden size={20} weight="duotone" />
                </span>

                <span
                  className={cn(
                    "leading-3.5 text-[10px] font-bold",
                    "text-indigo-950",
                    "sm:text-[11px] sm:leading-4",
                  )}
                >
                  {label}
                </span>
              </button>
            );
          })}
        </div>
      </fieldset>

      <label
        className={cn("mt-3 block", "text-[11px] font-bold text-indigo-950", "sm:mt-4 sm:text-xs")}
      >
        <span className="flex flex-wrap items-center gap-1.5">
          Annonce de l’offre d’emploi
          <span
            className={cn(
              "rounded-full bg-slate-100",
              "px-2 py-0.5",
              "text-[8px] font-semibold text-slate-500",
              "sm:text-[9px]",
            )}
          >
            Optionnel
          </span>
        </span>

        <span
          className={cn("mt-0.5 block", "text-[9px] font-normal text-slate-500", "sm:text-[10px]")}
        >
          Collez l’annonce pour recevoir des recommandations plus précises.
        </span>

        <span className="relative mt-1.5 block">
          <textarea
            value={jobDescription}
            onChange={(event) => {
              onJobDescriptionChange(event.target.value);
            }}
            rows={2}
            placeholder="Collez ici la description du poste, les missions et les compétences recherchées…"
            className={cn(
              "h-[66px] w-full resize-none rounded-xl",
              "border border-slate-200 bg-white",
              "px-3 py-2.5 pr-10",
              "text-xs leading-5 text-indigo-950",
              "outline-none transition",
              "placeholder:text-slate-400",
              "hover:border-indigo-200",
              "focus:border-indigo-500 focus:ring-2",
              "focus:ring-indigo-100",
              "sm:h-[76px] sm:px-4 sm:py-3 sm:pr-11 sm:text-sm",
            )}
          />

          <FileTextIcon
            aria-hidden
            size={21}
            weight="duotone"
            className={cn("pointer-events-none absolute bottom-3 right-3", "text-indigo-400")}
          />
        </span>
      </label>

      <button
        type="button"
        disabled={!complete}
        onClick={onContinue}
        className={cn(
          "mx-auto mt-3 flex min-h-10 w-full max-w-[280px]",
          "items-center justify-center gap-3",
          "rounded-xl",
          "bg-gradient-to-r from-indigo-700 via-blue-600 to-emerald-500",
          "px-6 text-sm font-bold text-white",
          "shadow-[0_8px_20px_rgba(79,70,229,0.22)]",
          "transition-all duration-200",
          "hover:-translate-y-0.5",
          "hover:shadow-[0_11px_25px_rgba(79,70,229,0.28)]",
          "focus-visible:outline-none focus-visible:ring-2",
          "focus-visible:ring-indigo-500 focus-visible:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-40",
          "disabled:hover:translate-y-0 disabled:hover:shadow-none",
          "sm:mt-4 sm:min-h-11 sm:text-base",
        )}
      >
        Continuer
        <ArrowRightIcon aria-hidden size={19} weight="bold" className="shrink-0" />
      </button>
    </section>
  );
};
